import { ChangeEvent, FC, ReactElement, SVGProps, useCallback, useMemo, useState } from 'react';
import {
  MultiSelect,
  PriceBandIcon,
  Prompt,
  SearchField,
  SnackbarStatus,
  Typography,
  useSnackbar,
} from '@weareopenr/component-library';
import { useAuthWrapper } from '@weareopenr/authkit';
import { useSearchTerms } from 'src/hooks/useSearchTerms';
import { deleteMenuItem } from 'src/api/deleteMenuItem/deleteMenuItem';
import Burger from 'src/assets/images/Burger';
import EmptyState from 'src/components/EmptyState';
import { NoResultsFound } from 'src/components/NoResultsFound';
import { ProductLibraryTable, ProductTableRow } from 'src/components/ProductLibraryTable';
import SkeletonContainer from 'src/components/SkeletonContainer';
import { FEATURE_FLAGS, ItemType } from 'src/constants';
import { useDebounce } from 'src/hooks/useDebounce/useDebounce';
import useProductLibraryItems from 'src/hooks/useProductLibraryItems/useProductLibraryItems';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';
import { RangeOption } from 'src/pages/Items/Items';
import styles from 'src/pages/Items/Items.module.scss';

export const EmptyOrNoResults = ({
  searchQuery,
  hasActiveFilters,
  emptyStateText,
  buttonText,
  Icon,
  onButtonClick,
}: {
  searchQuery?: string;
  hasActiveFilters: boolean;
  emptyStateText: string;
  buttonText: string;
  Icon: FC<SVGProps<SVGSVGElement>>;
  onButtonClick: () => void;
}): ReactElement => {
  if (searchQuery || hasActiveFilters) {
    return (
      <div className={styles['search-result']}>
        <NoResultsFound searchQuery={searchQuery} hasActiveFilters={hasActiveFilters} size="large" />
      </div>
    );
  }

  return (
    <div className={styles['empty-state-container']}>
      <EmptyState emptySectionText={emptyStateText} buttonText={buttonText} Icon={Icon} onClick={onButtonClick} />
    </div>
  );
};

export const ProductsTab = ({
  isSyncInProgress,
  onOpenItemWizard,
  rangeOptions,
}: {
  isSyncInProgress: boolean;
  onOpenItemWizard: () => void;
  rangeOptions: RangeOption[];
}): ReactElement => {
  const [searchQuery, setSearchQuery] = useState<string>();
  const [searchType, setSearchType] = useState<ItemType[]>([]);
  const [searchRanges, setSearchRanges] = useState<string[]>([]);
  const [isPromptVisible, setIsPromptVisible] = useState<boolean>(false);
  const [menuItemToDelete, setMenuItemToDelete] = useState<ProductTableRow | null>(null);
  const { showSnackbar } = useSnackbar();
  const { trackEvent, captureException } = useUserTracking();
  const { featureFlags } = useAuthWrapper();
  const { terms: searchTerms, setTerms: setSearchTerms, hasTerms } = useSearchTerms();

  // v2 commits a term on Enter and narrows by every committed term, so there is nothing to
  // debounce. v1 keeps searching as the user types.
  const isSearchV2 = featureFlags.includes(FEATURE_FLAGS.ITEM_SEARCH_V2);
  const debouncedSearchQuery = useDebounce(searchQuery);
  const activeSearch = isSearchV2 ? searchTerms : [debouncedSearchQuery].filter(Boolean);
  const hasSearch = isSearchV2 ? hasTerms : !!debouncedSearchQuery;
  const {
    data,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch,
    isLoading: areProductsLoading,
  } = useProductLibraryItems({
    searchQuery: activeSearch,
    type: searchType.length > 0 ? searchType : null,
    ranges: searchRanges.length > 0 ? searchRanges : null,
  });

  const typeOptions = [ItemType.STANDARD, ItemType.MODIFIER, ItemType.BUNDLE, ItemType.NON_FOOD].map((type, index) => ({
    id: type,
    label: `${type[0].toUpperCase()}${type.slice(1).toLowerCase()}`,
    display_order: index,
  }));

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value.toLowerCase());
  };

  const handlePromptToggle = useCallback(() => setIsPromptVisible((prevState) => !prevState), [setIsPromptVisible]);

  const handleOpenDeletePrompt = useCallback(
    (menuItem: ProductTableRow) => {
      setMenuItemToDelete(menuItem);
      handlePromptToggle();
    },
    [setMenuItemToDelete, handlePromptToggle],
  );

  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await deleteMenuItem(menuItemToDelete?.item_id);
      trackEvent('product_deleted', { productId: menuItemToDelete?.item_id, productName: menuItemToDelete?.name });
      showSnackbar({
        status: SnackbarStatus.SUCCESS,
        message: `You have successfully removed "${menuItemToDelete?.name}" from the item library.`,
      });

      await refetch();
    } catch (error) {
      captureException(error, { tags: { action: 'delete_product', productId: menuItemToDelete?.item_id } });
      error?.message?.includes('You cannot delete')
        ? showSnackbar({
            status: SnackbarStatus.ERROR,
            message: `Unable to delete ${menuItemToDelete?.name}`,
          })
        : showSnackbar({
            status: SnackbarStatus.ERROR,
            message: error?.message,
          });
    } finally {
      setIsDeleting(false);
      handlePromptToggle();
    }
  };

  const menuItems = useMemo(() => data?.pages.flatMap((page) => page.data), [data]);

  return (
    <>
      <div className={styles['search-container']}>
        {isSearchV2 ? (
          <SearchField
            isDisabled={false}
            placeholder="Search for a product"
            multiple
            terms={searchTerms}
            onTermsChange={setSearchTerms}
          />
        ) : (
          <SearchField isDisabled={false} placeholder="Search for a product" onChange={handleOnChange} />
        )}
        {rangeOptions.length > 0 && (
          <MultiSelect
            Icon={PriceBandIcon}
            label="Ranges"
            options={rangeOptions}
            selected={searchRanges}
            onChange={setSearchRanges}
            showSelectAllFooter
          />
        )}
        <MultiSelect
          Icon={PriceBandIcon}
          label="Types"
          options={typeOptions}
          selected={searchType}
          onChange={(ids: string[]) => setSearchType(ids as ItemType[])}
          showSelectAllFooter
        />
      </div>

      {areProductsLoading || isSyncInProgress ? (
        <SkeletonContainer hiddenTitle />
      ) : (
        <>
          {menuItems && menuItems.length > 0 ? (
            <main className={styles['results-container']}>
              {!isSearchV2 && debouncedSearchQuery && (
                <div className={styles['search-result']}>
                  <Typography size="large" inline>
                    Search results for{' '}
                  </Typography>
                  <Typography size="large" weight="semi-bold" inline>
                    &quot;{debouncedSearchQuery}&quot;
                  </Typography>
                </div>
              )}
              <Prompt
                promptHeading={`Are you sure you want to delete "${menuItemToDelete?.name}"?`}
                closePrompt={handlePromptToggle}
                open={isPromptVisible}
                buttonHandler={handleDelete}
                buttonText="Delete"
                buttonColour="tomato"
                backButton={true}
                size="small"
                isSubmitting={isDeleting}
              >
                You will remove this product from the product library and all menus it appears within.
              </Prompt>
              <ProductLibraryTable
                data={menuItems}
                onDelete={handleOpenDeletePrompt}
                fetchNextPage={fetchNextPage}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
              />
            </main>
          ) : (
            <EmptyOrNoResults
              searchQuery={isSearchV2 ? searchTerms.join(', ') : searchQuery}
              hasActiveFilters={hasSearch || searchType.length > 0 || searchRanges.length > 0}
              emptyStateText="Add your first product"
              buttonText="New product"
              Icon={Burger}
              onButtonClick={onOpenItemWizard}
            />
          )}
        </>
      )}
    </>
  );
};
